import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import MobileLayout from '../components/MobileLayout';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { useTranslation } from 'react-i18next';

const getStripePromise = () => {
  const isTestUser = localStorage.getItem('isTestUser') === 'true';
  const key = isTestUser
    ? process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_TEST
    : process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_LIVE;
  return loadStripe(key || '');
};

interface CreditPack {
  id: string;
  credits: number;
  price: number;
  label: string;
  popular?: boolean;
}

const Credits: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedPackage, setSelectedPackage] = useState<string>('topup_medium');
  const [isLoading, setIsLoading] = useState(false);
  const [currentBalance, setCurrentBalance] = useState<number>(0);

  const packs: CreditPack[] = [
    {
      id: 'topup_small',
      credits: 2000,
      price: 2.99,
      label: t('credits.small', 'Small'),
    },
    {
      id: 'topup_medium',
      credits: 5000,
      price: 4.99,
      label: t('credits.medium', 'Medium'),
      popular: true,
    },
    {
      id: 'topup_large',
      credits: 12000,
      price: 9.99,
      label: t('credits.large', 'Large'),
    },
    {
      id: 'topup_mega',
      credits: 30000,
      price: 19.99,
      label: t('credits.mega', 'Mega'),
    },
  ];

  useEffect(() => {
    fetchTokenBalance();
  }, []);

  const fetchTokenBalance = async () => {
    try {
      const response = await axios.get('/tokens/balance');
      setCurrentBalance(Number(response.data.currentBalance) || 0);
    } catch (error) {
      console.error('Failed to fetch token balance:', error);
    }
  };

  const getPerCredit = (pack: CreditPack) => {
    return ((pack.price / pack.credits) * 1000).toFixed(2);
  };

  const handlePurchase = async () => {
    const pack = packs.find((p) => p.id === selectedPackage);
    if (!pack) return;

    setIsLoading(true);

    try {
      const response = await axios.post('/subscriptions/top-up', {
        packId: selectedPackage,
        successUrl: `${window.location.origin}/credits/success`,
        cancelUrl: `${window.location.origin}/credits`,
      });

      const { sessionId, sessionUrl } = response.data;

      if (sessionUrl) {
        window.location.href = sessionUrl;
      } else if (sessionId) {
        const stripe = await getStripePromise();
        if (stripe) {
          const { error } = await stripe.redirectToCheckout({ sessionId });
          if (error) {
            console.error('Stripe redirect error:', error);
            alert(
              t('creditsTopUp.checkoutError', 'Failed to redirect to checkout. Please try again.'),
            );
          }
        }
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      alert(t('creditsTopUp.sessionError', 'Failed to create checkout session. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MobileLayout>
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-light-border dark:border-dark-border">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-light-surface dark:hover:bg-dark-surface rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-light-text dark:text-dark-text" />
          </button>
          <h1 className="text-lg font-semibold text-light-text dark:text-dark-text">
            {t('credits.title', 'Top Up Credits')}
          </h1>
          <div className="w-9" />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Current Balance */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-light-surface dark:bg-dark-surface px-4 py-2 rounded-full">
              <StarIcon className="w-5 h-5 text-yellow-500" />
              <span className="text-light-text-secondary dark:text-dark-text-secondary text-sm">
                {t('creditsTopUp.currentBalance', 'Current Balance:')}
              </span>
              <span className="text-light-text dark:text-dark-text font-semibold">
                {currentBalance.toLocaleString()}
              </span>
            </div>
          </div>

          {/* Info */}
          <p className="text-center text-light-text-secondary dark:text-dark-text-secondary text-sm mb-6">
            {t('credits.description', 'Top up your credit balance for more conversations.')}
          </p>

          {/* Credit Packs */}
          <div className="space-y-3 mb-8">
            {packs.map((pack) => (
              <button
                key={pack.id}
                onClick={() => setSelectedPackage(pack.id)}
                className={`w-full p-4 rounded-2xl border-2 transition-all ${
                  selectedPackage === pack.id
                    ? 'border-primary-500 bg-primary-500/10'
                    : 'border-light-border dark:border-dark-border bg-white dark:bg-dark-surface hover:border-gray-300 dark:hover:border-dark-border-hover'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedPackage === pack.id
                          ? 'border-primary-500'
                          : 'border-light-text-secondary dark:border-dark-text-secondary'
                      }`}
                    >
                      {selectedPackage === pack.id && (
                        <div className="w-3 h-3 rounded-full bg-primary-500" />
                      )}
                    </div>
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <StarIcon className="w-4 h-4 text-yellow-500" />
                        <span className="text-light-text dark:text-dark-text font-semibold">
                          {pack.credits.toLocaleString()} {t('credits.credits', 'credits')}
                        </span>
                        {pack.popular && (
                          <span className="bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded-full">
                            {t('creditsTopUp.bestValue', 'Best Value')}
                          </span>
                        )}
                      </div>
                      <p className="text-light-text-secondary dark:text-dark-text-secondary text-xs mt-1">
                        {pack.label} · ${getPerCredit(pack)}/
                        {t('credits.perThousand', '1K credits')}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-light-text dark:text-dark-text font-bold text-lg">
                      ${pack.price}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Purchase Button */}
          <button
            onClick={handlePurchase}
            disabled={isLoading}
            className="w-full py-4 bg-green-500 hover:bg-green-600 disabled:bg-green-500/50 text-white font-semibold rounded-2xl transition-colors flex items-center justify-center gap-2"
          >
            <StarIcon className="w-5 h-5" />
            {isLoading
              ? t('creditsTopUp.processing', 'Processing...')
              : t('creditsTopUp.addCredits', 'Add Credits')}
          </button>

          {/* Terms */}
          <p className="text-center text-light-text-secondary dark:text-dark-text-secondary text-xs mt-4">
            {t(
              'credits.neverExpire',
              'Credits never expire. Use them anytime for messages and features.',
            )}
          </p>
        </div>
      </div>
    </MobileLayout>
  );
};

export default Credits;
